import { api, handleApiError } from '../client'

export interface UserProfile {
  id: string
  email: string
  firstName: string
  lastName: string
  avatarUrl?: string
  timezone?: string
  language?: string
}

export interface UpdateProfileRequest {
  firstName?: string
  lastName?: string
  timezone?: string
  language?: string
}

export interface NotificationSettings {
  marketingEmails: boolean
  securityAlerts: boolean
  templateUpdates: boolean
  desktopNotifications: boolean
  soundEffects: boolean
}

export interface SecuritySettings {
  twoFactorEnabled: boolean
  lastPasswordChange?: string
  activeSessions: number
}

export interface ChangePasswordRequest {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

export interface TwoFactorSetupResponse {
  secret: string
  qrCodeUrl: string
  backupCodes: string[]
}

export interface AppearanceSettings {
  theme: 'light' | 'dark' | 'system'
  language: string
  dateFormat: string
  timeFormat: '12h' | '24h'
}

export interface Session {
  id: string
  device: string
  browser: string
  location?: string
  ipAddress: string
  lastActive: string
  isCurrent: boolean
}

export interface WorkspaceSettings {
  accountType: string
  companyName?: string
  companyIndustry?: string
  companySize?: string
  companyWebsite?: string
  appliedPresetId?: string
  appliedPresetName?: string
  includesDefaultAgents: boolean
  onboardingCompletedAt?: string
}

export interface UpdateWorkspaceRequest {
  accountType?: string
  companyName?: string
  companyIndustry?: string
  companySize?: string
  companyWebsite?: string
}

export interface ApplyPresetRequest {
  presetId: string
  includeDefaultAgents: boolean
}

export interface WorkspaceOperationResult {
  success: boolean
  message: string
  warnings?: string[]
}

export const settingsService = {
  /**
   * Get user profile
   */
  async getProfile(): Promise<UserProfile> {
    try {
      const response = await api.get<UserProfile>('/v1/settings/profile')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Update user profile
   */
  async updateProfile(data: UpdateProfileRequest): Promise<UserProfile> {
    try {
      const response = await api.put<UserProfile>('/v1/settings/profile', data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Upload avatar
   */
  async uploadAvatar(file: File): Promise<{ avatarUrl: string }> {
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await api.post<{ avatarUrl: string }>('/v1/settings/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Delete avatar
   */
  async deleteAvatar(): Promise<void> {
    try {
      await api.delete('/v1/settings/avatar')
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get notification settings
   */
  async getNotificationSettings(): Promise<NotificationSettings> {
    try {
      const response = await api.get<NotificationSettings>('/v1/settings/notifications')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Update notification settings
   */
  async updateNotificationSettings(settings: Partial<NotificationSettings>): Promise<NotificationSettings> {
    try {
      const response = await api.put<NotificationSettings>('/v1/settings/notifications', settings)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get security settings
   */
  async getSecuritySettings(): Promise<SecuritySettings> {
    try {
      const response = await api.get<SecuritySettings>('/v1/settings/security')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Change password
   */
  async changePassword(data: ChangePasswordRequest): Promise<void> {
    try {
      await api.post('/v1/settings/change-password', data)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Setup two-factor authentication
   */
  async setupTwoFactor(): Promise<TwoFactorSetupResponse> {
    try {
      const response = await api.post<TwoFactorSetupResponse>('/v1/settings/2fa/setup')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Enable two-factor authentication
   */
  async enableTwoFactor(code: string): Promise<{ backupCodes: string[] }> {
    try {
      const response = await api.post<{ backupCodes: string[] }>('/v1/settings/2fa/enable', { code })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Disable two-factor authentication
   */
  async disableTwoFactor(code: string): Promise<void> {
    try {
      await api.post('/v1/settings/2fa/disable', { code })
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get appearance settings
   */
  async getAppearanceSettings(): Promise<AppearanceSettings> {
    try {
      const response = await api.get<AppearanceSettings>('/v1/settings/appearance')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Update appearance settings
   */
  async updateAppearanceSettings(settings: Partial<AppearanceSettings>): Promise<AppearanceSettings> {
    try {
      const response = await api.put<AppearanceSettings>('/v1/settings/appearance', settings)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get active sessions
   */
  async getSessions(): Promise<Session[]> {
    try {
      const response = await api.get<Session[]>('/v1/settings/sessions')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Revoke a session
   */
  async revokeSession(sessionId: string): Promise<void> {
    try {
      await api.delete(`/v1/settings/sessions/${sessionId}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Revoke all other sessions
   */
  async revokeAllSessions(): Promise<void> {
    try {
      await api.post('/v1/settings/sessions/revoke-all')
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Delete account
   */
  async deleteAccount(password: string): Promise<void> {
    try {
      await api.post('/v1/settings/delete-account', { password })
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Export user data (GDPR)
   */
  async exportUserData(): Promise<Blob> {
    try {
      const response = await api.get<Blob>('/v1/settings/export-data', {
        responseType: 'blob',
      })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Get workspace settings
   */
  async getWorkspaceSettings(): Promise<WorkspaceSettings> {
    try {
      const response = await api.get<WorkspaceSettings>('/v1/settings/workspace')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Update workspace settings
   */
  async updateWorkspaceSettings(settings: UpdateWorkspaceRequest): Promise<WorkspaceSettings> {
    try {
      const response = await api.put<WorkspaceSettings>('/v1/settings/workspace', settings)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Apply or change industry preset
   */
  async applyPreset(request: ApplyPresetRequest): Promise<WorkspaceOperationResult> {
    try {
      const response = await api.post<WorkspaceOperationResult>('/v1/settings/workspace/apply-preset', request)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Reset workspace to default agents
   */
  async resetToDefaults(): Promise<WorkspaceOperationResult> {
    try {
      const response = await api.post<WorkspaceOperationResult>('/v1/settings/workspace/reset-defaults')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Re-run setup wizard
   */
  async rerunSetupWizard(): Promise<WorkspaceOperationResult> {
    try {
      const response = await api.post<WorkspaceOperationResult>('/v1/settings/workspace/rerun-wizard')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },
}
